import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";

import api from "/imports/api/client";

const BOOKINGS_TAB = "dashboard?tab=bookings&scope=upcoming";

// The club changed the booking while the client was moving it: nothing was moved.
const isBookingChanged = (err) => err?.data?.data?.reason === "BOOKING_CHANGED";

/**
 * Reschedule mode of the courts page (`?reschedule=<bookingId>`): the page lists the
 * times this paid court booking can move to, and a picked time opens a confirm step
 * instead of the cart. `info` is the server's description of the move (credit, allowed
 * venues, the booking being moved), refreshed with every availability load.
 */
export default function useCourtReschedule({ slug, bookingId, onStale }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [info, setInfo] = useState(null);
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const active = !!bookingId;
  const backToBookings = () => navigate(`/${slug}/${BOOKINGS_TAB}`);

  const select = (slot, date) => setTarget({ slot, date });
  const close = () => {
    if (!submitting) setTarget(null);
  };

  // The club changed or cancelled the booking while this page was open: there is nothing
  // left to move, so back to the bookings list, as the confirm step does.
  const leaveIfChanged = (err) => {
    if (!isBookingChanged(err)) return false;
    enqueueSnackbar(err.message, { variant: "warning" });
    backToBookings();
    return true;
  };

  const confirm = async () => {
    if (!target) return;
    const { slot, date } = target;
    setSubmitting(true);
    try {
      const result = await api.rescheduleBooking(slug, bookingId, {
        slot: {
          date,
          time: slot.time,
          facilityId: slot.facilityId,
          // "Any court" (or a lone free court) is the court picker's choice; a court the
          // client tapped is theirs.
          facilityAuto: !!slot.facilityAuto,
          locationId: slot.locationId
        }
      });
      enqueueSnackbar(
        result.requiresPayment
          ? "Your court is moved and your payment is in your wallet, but we couldn't apply it — please use Pay Now with your wallet on the new booking."
          : `Court moved to ${DateTime.fromISO(date).toFormat("ccc d LLL")} at ${slot.time}.`,
        { variant: result.requiresPayment ? "warning" : "success" }
      );
      backToBookings();
    } catch (err) {
      setSubmitting(false);
      if (isBookingChanged(err)) {
        enqueueSnackbar(err.message, { variant: "warning" });
        backToBookings();
        return;
      }
      enqueueSnackbar(err.message || "Couldn't move your court booking", { variant: "error" });
      // The court was taken or stopped qualifying while the client decided: show the day as it is now.
      setTarget(null);
      onStale();
    }
  };

  return { active, bookingId, info, setInfo, target, select, close, confirm, submitting, cancel: backToBookings, leaveIfChanged };
}
